<script setup lang="ts">
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
</script>

<template>
  <!-- Buffer Accounts Section -->
  <main id="buffer-account-main" class="flex-1 p-5 overflow-y-auto">
    <!-- Buffer Account Overview -->
    <div
      id="buffer-overview"
      class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6"
    >
      <div class="bg-white p-5 rounded-md border border-light-gray">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-600">{{ $t('government.administration.bufferAccounts.overview.totalBufferAllocation') }}</p>
            <p class="text-2xl font-medium text-dark-navy">2.0%</p>
          </div>
          <i class="fa-solid fa-shield-alt text-forest-green text-xl"></i>
        </div>
      </div>
      <div class="bg-white p-5 rounded-md border border-light-gray">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-600">{{ $t('government.administration.bufferAccounts.overview.activeSectors') }}</p>
            <p class="text-2xl font-medium text-dark-navy">8</p>
          </div>
          <i class="fa-solid fa-industry text-electric-blue text-xl"></i>
        </div>
      </div>
      <div class="bg-white p-5 rounded-md border border-light-gray">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-600">{{ $t('government.administration.bufferAccounts.overview.bufferCredits') }}</p>
            <p class="text-2xl font-medium text-dark-navy">49,000</p>
          </div>
          <i class="fa-solid fa-coins text-forest-green text-xl"></i>
        </div>
      </div>
    </div>

    <!-- Buffer Configuration -->
    <div
      id="buffer-config"
      class="bg-white p-5 rounded-md border border-light-gray mb-6"
    >
      <div class="flex justify-between items-center mb-6">
        <h3 class="text-lg font-medium text-dark-navy">
          {{ $t('government.administration.bufferAccounts.configuration.title') }}
        </h3>
        <button
          class="px-4 py-2 bg-forest-green text-white rounded-md hover:bg-green-700 transition-colors"
        >
          <i class="fa-solid fa-save mr-2"></i>
          {{ $t('government.administration.bufferAccounts.configuration.saveChanges') }}
        </button>
      </div>

      <!-- Global Settings -->
      <div id="global-settings" class="mb-6 p-4 bg-gray-50 rounded-md">
        <h4 class="font-medium text-dark-navy mb-4">{{ $t('government.administration.bufferAccounts.globalSettings.title') }}</h4>
        <div class="flex items-center space-x-4">
          <label class="flex items-center">
            <input
              type="checkbox"
              class="mr-2 w-4 h-4 text-forest-green"
              checked
            />
            <span class="text-sm">{{ $t('government.administration.bufferAccounts.globalSettings.applyToAllSectors') }}</span>
          </label>
          <div class="flex items-center space-x-2">
            <label class="text-sm font-medium">{{ $t('government.administration.bufferAccounts.globalSettings.globalPercentage') }}</label>
            <input
              type="number"
              value="2.0"
              step="0.1"
              min="0"
              max="100"
              class="px-3 py-2 border border-light-gray rounded-md w-20 text-center"
            />
            <span class="text-sm text-gray-600">%</span>
          </div>
        </div>
      </div>

      <!-- Sector-Specific Settings -->
      <div id="sector-settings">
        <div class="flex items-center justify-between mb-4">
          <h4 class="font-medium text-dark-navy">
            {{ $t('government.administration.bufferAccounts.sectorSettings.title') }}
          </h4>
          <label class="flex items-center">
            <input type="checkbox" class="mr-2 w-4 h-4 text-forest-green" />
            <span class="text-sm">{{ $t('government.administration.bufferAccounts.sectorSettings.enableSectorSpecific') }}</span>
          </label>
        </div>

        <div class="space-y-4">
          <!-- Renewable Energy -->
          <div
            id="renewable-energy-buffer"
            class="flex items-center justify-between p-4 border border-light-gray rounded-md"
          >
            <div class="flex items-center space-x-4">
              <input type="checkbox" class="w-4 h-4 text-forest-green" />
              <div>
                <h5 class="font-medium text-dark-navy">{{ $t('government.administration.bufferAccounts.sectors.renewableEnergy.title') }}</h5>
                <p class="text-sm text-gray-600">{{ $t('government.administration.bufferAccounts.sectors.renewableEnergy.description') }}</p>
              </div>
            </div>
            <div class="flex items-center space-x-2">
              <input
                type="number"
                value="1.5"
                step="0.1"
                min="0"
                max="100"
                class="px-3 py-2 border border-light-gray rounded-md w-20 text-center"
              />
              <span class="text-sm text-gray-600">%</span>
            </div>
          </div>

          <!-- Forestry -->
          <div
            id="forestry-buffer"
            class="flex items-center justify-between p-4 border border-light-gray rounded-md"
          >
            <div class="flex items-center space-x-4">
              <input
                type="checkbox"
                class="w-4 h-4 text-forest-green"
                checked
              />
              <div>
                <h5 class="font-medium text-dark-navy">{{ $t('government.administration.bufferAccounts.sectors.forestryLandUse.title') }}</h5>
                <p class="text-sm text-gray-600">
                  {{ $t('government.administration.bufferAccounts.sectors.forestryLandUse.description') }}
                </p>
              </div>
            </div>
            <div class="flex items-center space-x-2">
              <input
                type="number"
                value="3.0"
                step="0.1"
                min="0"
                max="100"
                class="px-3 py-2 border border-light-gray rounded-md w-20 text-center"
              />
              <span class="text-sm text-gray-600">%</span>
            </div>
          </div>

          <!-- Agriculture -->
          <div
            id="agriculture-buffer"
            class="flex items-center justify-between p-4 border border-light-gray rounded-md"
          >
            <div class="flex items-center space-x-4">
              <input
                type="checkbox"
                class="w-4 h-4 text-forest-green"
                checked
              />
              <div>
                <h5 class="font-medium text-dark-navy">{{ $t('government.administration.bufferAccounts.sectors.agriculture.title') }}</h5>
                <p class="text-sm text-gray-600">
                  {{ $t('government.administration.bufferAccounts.sectors.agriculture.description') }}
                </p>
              </div>
            </div>
            <div class="flex items-center space-x-2">
              <input
                type="number"
                value="2.5"
                step="0.1"
                min="0"
                max="100"
                class="px-3 py-2 border border-light-gray rounded-md w-20 text-center"
              />
              <span class="text-sm text-gray-600">%</span>
            </div>
          </div>

          <!-- Waste Management -->
          <div
            id="waste-buffer"
            class="flex items-center justify-between p-4 border border-light-gray rounded-md"
          >
            <div class="flex items-center space-x-4">
              <input type="checkbox" class="w-4 h-4 text-forest-green" />
              <div>
                <h5 class="font-medium text-dark-navy">{{ $t('government.administration.bufferAccounts.sectors.wasteManagement.title') }}</h5>
                <p class="text-sm text-gray-600">{{ $t('government.administration.bufferAccounts.sectors.wasteManagement.description') }}</p>
              </div>
            </div>
            <div class="flex items-center space-x-2">
              <input
                type="number"
                value="2.0"
                step="0.1"
                min="0"
                max="100"
                class="px-3 py-2 border border-light-gray rounded-md w-20 text-center"
              />
              <span class="text-sm text-gray-600">%</span>
            </div>
          </div>

          <!-- Manufacturing -->
          <div
            id="manufacturing-buffer"
            class="flex items-center justify-between p-4 border border-light-gray rounded-md"
          >
            <div class="flex items-center space-x-4">
              <input type="checkbox" class="w-4 h-4 text-forest-green" />
              <div>
                <h5 class="font-medium text-dark-navy">{{ $t('government.administration.bufferAccounts.sectors.manufacturing.title') }}</h5>
                <p class="text-sm text-gray-600">
                  {{ $t('government.administration.bufferAccounts.sectors.manufacturing.description') }}
                </p>
              </div>
            </div>
            <div class="flex items-center space-x-2">
              <input
                type="number"
                value="1.8"
                step="0.1"
                min="0"
                max="100"
                class="px-3 py-2 border border-light-gray rounded-md w-20 text-center"
              />
              <span class="text-sm text-gray-600">%</span>
            </div>
          </div>

          <!-- Transport -->
          <div
            id="transport-buffer"
            class="flex items-center justify-between p-4 border border-light-gray rounded-md"
          >
            <div class="flex items-center space-x-4">
              <input type="checkbox" class="w-4 h-4 text-forest-green" />
              <div>
                <h5 class="font-medium text-dark-navy">{{ $t('government.administration.bufferAccounts.sectors.transport.title') }}</h5>
                <p class="text-sm text-gray-600">
                  {{ $t('government.administration.bufferAccounts.sectors.transport.description') }}
                </p>
              </div>
            </div>
            <div class="flex items-center space-x-2">
              <input
                type="number"
                value="2.2"
                step="0.1"
                min="0"
                max="100"
                class="px-3 py-2 border border-light-gray rounded-md w-20 text-center"
              />
              <span class="text-sm text-gray-600">%</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Current Allocations Summary -->
    <div id="allocations-summary" class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div class="bg-white p-5 rounded-md border border-light-gray">
        <h3 class="text-lg font-medium text-dark-navy mb-4">
          {{ $t('government.administration.bufferAccounts.summary.activeAllocations.title') }}
        </h3>
        <div class="space-y-3">
          <div
            class="flex justify-between items-center p-3 bg-green-50 rounded-md"
          >
            <div>
              <p class="font-medium">Forestry & Land Use</p>
              <p class="text-sm text-gray-600">147,000 {{ $t('government.administration.bufferAccounts.summary.activeAllocations.forestryCredits') }}</p>
            </div>
            <span
              class="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm"
              >3.0%</span
            >
          </div>
          <div
            class="flex justify-between items-center p-3 bg-blue-50 rounded-md"
          >
            <div>
              <p class="font-medium">Agriculture</p>
              <p class="text-sm text-gray-600">73,500 {{ $t('government.administration.bufferAccounts.summary.activeAllocations.agricultureCredits') }}</p>
            </div>
            <span
              class="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
              >2.5%</span
            >
          </div>
          <div
            class="flex justify-between items-center p-3 bg-yellow-50 rounded-md"
          >
            <div>
              <p class="font-medium">Transport</p>
              <p class="text-sm text-gray-600">32,200 {{ $t('government.administration.bufferAccounts.summary.activeAllocations.transportCredits') }}</p>
            </div>
            <span
              class="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm"
              >2.2%</span
            >
          </div>
        </div>
      </div>

      <div class="bg-white p-5 rounded-md border border-light-gray">
        <h3 class="text-lg font-medium text-dark-navy mb-4">
          {{ $t('government.administration.bufferAccounts.summary.activity.title') }}
        </h3>
        <div class="space-y-3">
          <div
            class="flex items-center justify-between p-3 bg-gray-50 rounded-md"
          >
            <div>
              <p class="font-medium">{{ $t('government.administration.bufferAccounts.summary.activity.creditsAdded') }}</p>
              <p class="text-sm text-gray-600">{{ $t('government.administration.bufferAccounts.summary.activity.last30Days') }}</p>
            </div>
            <span class="font-medium text-forest-green">+12,500</span>
          </div>
          <div
            class="flex items-center justify-between p-3 bg-gray-50 rounded-md"
          >
            <div>
              <p class="font-medium">{{ $t('government.administration.bufferAccounts.summary.activity.creditsReleased') }}</p>
              <p class="text-sm text-gray-600">{{ $t('government.administration.bufferAccounts.summary.activity.last30Days') }}</p>
            </div>
            <span class="font-medium text-electric-blue">-8,200</span>
          </div>
          <div
            class="flex items-center justify-between p-3 bg-gray-50 rounded-md"
          >
            <div>
              <p class="font-medium">{{ $t('government.administration.bufferAccounts.summary.activity.netChange') }}</p>
              <p class="text-sm text-gray-600">{{ $t('government.administration.bufferAccounts.summary.activity.last30Days') }}</p>
            </div>
            <span class="font-medium text-forest-green">+4,300</span>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>
